import { create } from 'zustand';
import { useCallback, useMemo } from 'react';
import { locationCache } from '../services/LocationCacheService';
import { AnalyticsService } from '../services/AnalyticsService';

// === Types ===

type GeoStatus = 'pending' | 'success' | 'denied' | 'timeout' | 'unsupported';

interface GeoState {
  status: GeoStatus;
  location: { latitude: number; longitude: number; accuracy?: number } | null;
  error: string | null;
  startTime?: number;
}

declare global {
  interface Window {
    __GEO_STATE__?: GeoState;
  }
}

export interface Location {
  latitude: number;
  longitude: number;
  cityName?: string;
  shouldCenter?: boolean;
  accuracy?: number;
}

interface CachedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
  accuracy?: number;
  cityName?: string;
}

export type LocationSource = 'geolocation' | 'search' | 'map_move' | 'cache' | 'fallback';

interface LocationState {
  location: Location | undefined;
  loading: boolean;
  error: 'geolocation_denied' | 'geolocation_timeout' | null;

  setLocation: (lat: number, lon: number, shouldCenter?: boolean, source?: LocationSource) => Promise<void>;
  requestGeoLocation: () => Promise<void>;
  clearShouldCenter: () => void;
  clearError: () => void;
}

// === Constants ===

const FALLBACK_LOCATION = {
  latitude: 5.96318194,
  longitude: 80.70767406,
  cityName: 'Hiriketiya'
};

const GEO_BUDGET_MS = 7000;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h

const isLocationRecent = (timestamp: number): boolean =>
  (Date.now() - timestamp) < CACHE_TTL_MS;

// === Debounced getCityName (Nominatim 1 req/s) ===

let lastNominatimCall = 0;

async function getCityNameDebounced(lat: number, lon: number): Promise<string | undefined> {
  try {
    // Check IndexedDB cache first
    const cacheKey = `${lat.toFixed(4)},${lon.toFixed(4)}`;
    const cachedAddress = await locationCache.getAddress(cacheKey);
    if (cachedAddress) return cachedAddress.components.city;

    // Debounce: enforce 1s between Nominatim calls
    const now = Date.now();
    const sinceLastCall = now - lastNominatimCall;
    if (sinceLastCall < 1000) {
      await new Promise(r => setTimeout(r, 1000 - sinceLastCall));
    }
    lastNominatimCall = Date.now();

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
    );
    const data = await response.json();
    const cityName = data.address.city || data.address.town || data.address.village;

    if (cityName) {
      await locationCache.saveAddress(cacheKey, {
        lat,
        lng: lon,
        formatted: data.display_name,
        components: {
          city: cityName,
          country: data.address.country,
          postcode: data.address.postcode
        }
      });
    }

    return cityName;
  } catch (error) {
    console.warn('🌍 [LocationStore] Erreur getCityName:', error);
    return undefined;
  }
}

// === waitForGeo: event-based, one Promise, one listener, one timeout ===

function waitForGeo(budgetMs: number): Promise<GeoState> {
  const geoState = window.__GEO_STATE__;

  // Already resolved (shell finished before React mount)
  if (geoState && geoState.status !== 'pending') {
    return Promise.resolve(geoState);
  }

  // Calculate remaining budget from shell start
  const elapsed = Date.now() - (geoState?.startTime ?? Date.now());
  const remaining = Math.max(500, budgetMs - elapsed);

  return new Promise((resolve) => {
    const cleanup = () => {
      window.removeEventListener('geo-resolved', handler);
      clearTimeout(timer);
    };

    const handler = (e: Event) => {
      cleanup();
      resolve((e as CustomEvent).detail);
    };

    const timer = setTimeout(() => {
      cleanup();
      resolve(window.__GEO_STATE__ ?? { status: 'timeout', location: null, error: 'budget exceeded' });
    }, remaining);

    window.addEventListener('geo-resolved', handler, { once: true });
  });
}

// === Zustand Store ===

export const useLocationStore = create<LocationState>()((set, get) => ({
  location: undefined,
  loading: true,
  error: null,

  clearShouldCenter: () => {
    const current = get().location;
    if (current?.shouldCenter) {
      set({ location: { ...current, shouldCenter: false } });
    }
  },

  clearError: () => set({ error: null }),

  setLocation: async (lat, lon, shouldCenter = true, source: LocationSource = 'search') => {
    // Immediate position update
    set({
      location: { latitude: lat, longitude: lon, shouldCenter },
      loading: false,
      error: null,
    });

    // Track analytics (background, non-blocking)
    try {
      AnalyticsService.trackConnexionZone({ latitude: lat, longitude: lon }, source);
    } catch { /* non-critical */ }

    // Enrich cityName + save cache (background)
    try {
      const cityName = await getCityNameDebounced(lat, lon);

      if (cityName) {
        set(state => {
          if (state.location?.cityName === cityName) return state;
          return { location: state.location ? { ...state.location, cityName } : state.location };
        });
      }

      const toSave: CachedLocation = { latitude: lat, longitude: lon, timestamp: Date.now(), cityName };
      localStorage.setItem('userLocation', JSON.stringify(toSave));
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'userLocation',
        newValue: JSON.stringify(toSave)
      }));
    } catch { /* non-critical */ }
  },

  requestGeoLocation: async () => {
    if (!navigator.geolocation) {
      set({ error: 'geolocation_denied' as const, loading: false });
      return;
    }

    set({ loading: true, error: null });

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000
        });
      });

      const { latitude, longitude } = position.coords;
      await get().setLocation(latitude, longitude, true, 'geolocation');
    } catch (geoError: unknown) {
      const code = (geoError as GeolocationPositionError).code;
      set({
        error: code === 1 ? 'geolocation_denied' : 'geolocation_timeout',
        loading: false
      });
    }
  }
}));

// === Hooks ===

export const useGeolocation = () => {
  const location = useLocationStore(state => state.location);
  const loading = useLocationStore(state => state.loading);
  const error = useLocationStore(state => state.error);

  const updateFromMap = useCallback((lat: number, lon: number) => {
    useLocationStore.getState().setLocation(lat, lon, true, 'map_move');
  }, []);

  return useMemo(
    () => ({ location, loading, error, updateFromMap }),
    [location, loading, error, updateFromMap]
  );
};

// Legacy selectors (kept for any direct imports)
export const useLocation = () => useLocationStore(state => state.location);
export const useLocationLoading = () => useLocationStore(state => state.loading);
export const useLocationError = () => useLocationStore(state => state.error);

// === Initialization ===

export const initializeLocationStore = () => {
  const loadInitialLocation = async () => {
    try {
      const isDeepLink = /^\/(event|events|organizer)\//.test(window.location.pathname);

      // Deep link: skip geo wait, fallback immediately (no toast)
      if (isDeepLink) {
        const cached = loadCache();
        if (cached) {
          await useLocationStore.getState().setLocation(cached.latitude, cached.longitude, false, 'cache');
          if (cached.cityName) {
            useLocationStore.setState(s => ({
              location: s.location ? { ...s.location, cityName: cached.cityName } : s.location
            }));
          }
        } else {
          useLocationStore.setState({ location: { ...FALLBACK_LOCATION }, loading: false });
          AnalyticsService.trackConnexionZone(FALLBACK_LOCATION, 'fallback');
        }
        return;
      }

      // Wait for shell geo (event-based, no polling)
      const geoResult = await waitForGeo(GEO_BUDGET_MS);

      // Success: use geolocation
      if (geoResult.status === 'success' && geoResult.location) {
        const { latitude, longitude } = geoResult.location;
        await useLocationStore.getState().setLocation(latitude, longitude, true, 'geolocation');
        return;
      }

      // Denied: fallback + show toast
      if (geoResult.status === 'denied') {
        applyFallback('geolocation_denied');
        return;
      }

      // Timeout: try cache first, then fallback
      if (geoResult.status === 'timeout' || geoResult.status === 'unsupported') {
        const cached = loadCache();
        if (cached) {
          await useLocationStore.getState().setLocation(cached.latitude, cached.longitude, false, 'cache');
          if (cached.cityName) {
            useLocationStore.setState(s => ({
              location: s.location ? { ...s.location, cityName: cached.cityName } : s.location
            }));
          }
          // Show timeout toast (position came from cache, not fresh geo)
          useLocationStore.setState({ error: 'geolocation_timeout' });
        } else {
          applyFallback('geolocation_timeout');
        }
        return;
      }

      // Still pending after budget (user ignoring permission popup)
      // Use cache or fallback silently, then listen for late response
      if (geoResult.status === 'pending') {
        const cached = loadCache();
        if (cached) {
          await useLocationStore.getState().setLocation(cached.latitude, cached.longitude, false, 'cache');
          if (cached.cityName) {
            useLocationStore.setState(s => ({
              location: s.location ? { ...s.location, cityName: cached.cityName } : s.location
            }));
          }
        } else {
          useLocationStore.setState({ location: { ...FALLBACK_LOCATION }, loading: false });
        }

        // Listen for late geo response (user eventually answers the popup)
        const lateHandler = async (e: Event) => {
          const detail = (e as CustomEvent<GeoState>).detail;
          if (detail.status === 'success' && detail.location) {
            const { latitude, longitude } = detail.location;
            await useLocationStore.getState().setLocation(latitude, longitude, true, 'geolocation');
            useLocationStore.setState({ error: null });
          } else if (detail.status === 'denied') {
            useLocationStore.setState({ error: 'geolocation_denied' });
            AnalyticsService.trackConnexionZone(FALLBACK_LOCATION, 'fallback');
          }
        };
        window.addEventListener('geo-resolved', lateHandler, { once: true });

        // Safety: remove listener after 30s
        const safetyTimer = setTimeout(() => {
          window.removeEventListener('geo-resolved', lateHandler);
          // If still on fallback after 30s, show toast
          const loc = useLocationStore.getState().location;
          if (loc?.latitude === FALLBACK_LOCATION.latitude && loc?.longitude === FALLBACK_LOCATION.longitude) {
            useLocationStore.setState({ error: 'geolocation_denied' });
          }
        }, 30000);

        // Store cleanup ref for unmount
        cleanupLateListener = () => {
          window.removeEventListener('geo-resolved', lateHandler);
          clearTimeout(safetyTimer);
        };
        return;
      }

      // Catch-all fallback
      applyFallback('geolocation_denied');

    } catch (error) {
      console.error('🌍 [LocationStore] Erreur chargement initial:', error);
      useLocationStore.setState({ loading: false, error: 'geolocation_denied' });
    }
  };

  let cleanupLateListener: (() => void) | null = null;

  // Helper: load cached location from localStorage
  function loadCache(): CachedLocation | null {
    try {
      const saved = localStorage.getItem('userLocation');
      if (!saved) return null;
      const cached: CachedLocation = JSON.parse(saved);
      return isLocationRecent(cached.timestamp) ? cached : null;
    } catch {
      return null;
    }
  }

  // Helper: apply fallback + set error for toast
  function applyFallback(errorType: 'geolocation_denied' | 'geolocation_timeout') {
    const cached = loadCache();
    if (cached) {
      useLocationStore.setState({
        location: { latitude: cached.latitude, longitude: cached.longitude, cityName: cached.cityName },
        loading: false,
        error: errorType
      });
      AnalyticsService.trackConnexionZone({ latitude: cached.latitude, longitude: cached.longitude }, 'cache');
    } else {
      useLocationStore.setState({
        location: { ...FALLBACK_LOCATION },
        loading: false,
        error: errorType
      });
      AnalyticsService.trackConnexionZone(FALLBACK_LOCATION, 'fallback');
    }
  }

  loadInitialLocation();

  // Cross-tab sync
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'userLocation' && e.newValue) {
      try {
        const newLocation: CachedLocation = JSON.parse(e.newValue);
        useLocationStore.setState({
          location: {
            latitude: newLocation.latitude,
            longitude: newLocation.longitude,
            cityName: newLocation.cityName,
            accuracy: newLocation.accuracy
          }
        });
      } catch { /* ignore */ }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    cleanupLateListener?.();
  };
};
